import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { BASE_URL } from '../config';

const Card = ({ children, className = "" }) => (
  <div className={`bg-white rounded-lg border border-gray-200 ${className}`}>
    {children}
  </div>
);

const Button = ({ children, onClick, className = "", disabled = false }) => (
  <button
    className={`px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors ${
      disabled ? 'opacity-50 cursor-not-allowed' : ''
    } ${className}`}
    onClick={onClick}
    disabled={disabled}
  >
    {children}
  </button>
);

const SchedulerTriggers = () => {
  const [triggers, setTriggers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedTrigger, setSelectedTrigger] = useState(null);
  const [editBody, setEditBody] = useState('');
  const [jsonError, setJsonError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [runningJobKey, setRunningJobKey] = useState(null);
  const [runToast, setRunToast] = useState(null);
  const { apiKey } = useAuth();

  useEffect(() => {
    fetchTriggers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchTriggers = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(`${BASE_URL}/scout/scheduler-triggers`, {
        headers: {
          'Authorization': `Bearer ${apiKey.key}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setTriggers(data);
      } else {
        const errorData = await response.json();
        setError(typeof errorData.detail === 'string' ? errorData.detail : 'Failed to fetch triggers');
      }
    } catch (err) {
      console.error('Error fetching triggers:', err);
      setError('Failed to fetch triggers. Please check your connection.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditTrigger = async (trigger) => {
    setSelectedTrigger(trigger);
    setJsonError('');
    if (trigger.target_type !== 'http') {
      setEditBody('');
      setIsEditModalOpen(true);
      return;
    }
    try {
      const response = await fetch(
        `${BASE_URL}/scout/scheduler-triggers/${trigger.location}/${trigger.job_id}`,
        {
          headers: { 'Authorization': `Bearer ${apiKey.key}` }
        }
      );
      if (response.ok) {
        const fullTrigger = await response.json();
        const bodyToShow = fullTrigger.body_json
          ? JSON.stringify(fullTrigger.body_json, null, 2)
          : (fullTrigger.body || '');
        setEditBody(bodyToShow);
      } else {
        setEditBody(trigger.body || '');
      }
    } catch (err) {
      setEditBody(trigger.body || '');
    }
    setIsEditModalOpen(true);
  };

  const handleSaveBody = async () => {
    if (!selectedTrigger || selectedTrigger.target_type !== 'http') return;

    try {
      let bodyToSave = editBody.trim();
      if (bodyToSave) {
        try {
          JSON.parse(bodyToSave);
          setJsonError('');
        } catch (e) {
          setJsonError('Invalid JSON format');
          return;
        }
      }

      setIsSaving(true);
      const response = await fetch(
        `${BASE_URL}/scout/scheduler-triggers/${selectedTrigger.location}/${selectedTrigger.job_id}`,
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${apiKey.key}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ body: bodyToSave })
        }
      );

      if (response.ok) {
        setIsEditModalOpen(false);
        setSelectedTrigger(null);
        setEditBody('');
        fetchTriggers();
      } else {
        const errorData = await response.json();
        setJsonError(typeof errorData.detail === 'string' ? errorData.detail : 'Failed to update');
      }
    } catch (err) {
      console.error('Error saving:', err);
      setJsonError('Failed to save. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRunTrigger = async (trigger) => {
    const key = `${trigger.location}-${trigger.job_id}`;
    setRunningJobKey(key);
    setRunToast(null);
    try {
      const response = await fetch(
        `${BASE_URL}/scout/scheduler-triggers/${trigger.location}/${trigger.job_id}/run`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${apiKey.key}` }
        }
      );
      if (response.ok) {
        setRunToast({ type: 'success', message: `Job "${trigger.job_id}" triggered successfully.` });
        fetchTriggers();
      } else {
        const errorData = await response.json();
        setRunToast({ type: 'error', message: typeof errorData.detail === 'string' ? errorData.detail : 'Failed to trigger job.' });
      }
    } catch (err) {
      console.error('Error running trigger:', err);
      setRunToast({ type: 'error', message: 'Failed to trigger job. Please check your connection.' });
    } finally {
      setRunningJobKey(null);
      setTimeout(() => setRunToast(null), 5000);
    }
  };

  const filteredTriggers = triggers.filter(
    (t) =>
      t.job_id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.location?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-8 py-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Cloud Scheduler Triggers</h1>
        </div>

        {runToast && (
          <div
            className={`mb-4 px-4 py-3 rounded-md text-sm font-medium ${
              runToast.type === 'success'
                ? 'bg-green-100 text-green-800 border border-green-200'
                : 'bg-red-100 text-red-800 border border-red-200'
            }`}
          >
            {runToast.message}
          </div>
        )}

        <Card>
          <div className="p-6">
            <div className="mb-4">
              <input
                type="text"
                placeholder="Search triggers by name or location..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md"
              />
            </div>

            {isLoading ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                <p className="mt-2 text-gray-600">Loading triggers...</p>
              </div>
            ) : error ? (
              <div className="text-center py-8 text-red-600">{error}</div>
            ) : filteredTriggers.length === 0 ? (
              <div className="text-center py-8 text-gray-600">
                {searchQuery ? 'No triggers found matching your search.' : 'No triggers found.'}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 text-gray-600 font-medium">Job</th>
                      <th className="text-left py-3 px-4 text-gray-600 font-medium">Location</th>
                      <th className="text-left py-3 px-4 text-gray-600 font-medium">Schedule</th>
                      <th className="text-left py-3 px-4 text-gray-600 font-medium">State</th>
                      <th className="text-left py-3 px-4 text-gray-600 font-medium">Type</th>
                      <th className="text-left py-3 px-4 text-gray-600 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTriggers.map((trigger) => (
                      <tr key={`${trigger.location}-${trigger.job_id}`} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4 font-mono text-sm">{trigger.job_id}</td>
                        <td className="py-3 px-4 text-sm">{trigger.location}</td>
                        <td className="py-3 px-4 text-sm">{trigger.schedule}</td>
                        <td className="py-3 px-4">
                          <span
                            className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                              trigger.state === 'ENABLED'
                                ? 'bg-green-100 text-green-700'
                                : 'bg-gray-100 text-gray-700'
                            }`}
                          >
                            {trigger.state}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-sm">{trigger.target_type || '-'}</td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <Button
                              onClick={() => handleEditTrigger(trigger)}
                              className="!bg-blue-600"
                            >
                              {trigger.target_type === 'http' ? 'Edit Body' : 'View'}
                            </Button>
                            <Button
                              onClick={() => handleRunTrigger(trigger)}
                              disabled={runningJobKey === `${trigger.location}-${trigger.job_id}`}
                              className="!bg-green-600 hover:!bg-green-700"
                            >
                              {runningJobKey === `${trigger.location}-${trigger.job_id}` ? 'Running...' : 'Run Now'}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Edit Modal */}
      {isEditModalOpen && selectedTrigger && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setIsEditModalOpen(false);
              setSelectedTrigger(null);
              setEditBody('');
              setJsonError('');
            }
          }}
        >
          <Card className="w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
            <div className="p-6 overflow-y-auto">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Edit Trigger: {selectedTrigger.job_id}
              </h2>
              <p className="text-sm text-gray-600 mb-4">
                Location: {selectedTrigger.location} • {selectedTrigger.uri && `URI: ${selectedTrigger.uri}`}
              </p>

              {selectedTrigger.target_type === 'http' ? (
                <>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    HTTP Request Body (JSON)
                  </label>
                  <textarea
                    value={editBody}
                    onChange={(e) => setEditBody(e.target.value)}
                    className={`w-full h-64 p-3 border rounded-md font-mono text-sm resize-none ${
                      jsonError ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder='{"key": "value"}'
                  />
                  {jsonError && (
                    <p className="mt-1 text-sm text-red-600">{jsonError}</p>
                  )}
                </>
              ) : (
                <p className="text-gray-600">
                  This trigger uses {selectedTrigger.target_type || 'a non-HTTP'} target. Body editing is only available for HTTP targets.
                </p>
              )}

              <div className="flex justify-end space-x-2 mt-4">
                <Button
                  onClick={() => {
                    setIsEditModalOpen(false);
                    setSelectedTrigger(null);
                    setEditBody('');
                    setJsonError('');
                  }}
                  className="!bg-gray-500"
                >
                  Cancel
                </Button>
                {selectedTrigger.target_type === 'http' && (
                  <Button
                    onClick={handleSaveBody}
                    disabled={isSaving}
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </Button>
                )}
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default SchedulerTriggers;
